import * as Sentry from '@sentry/react';
import * as React from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';

import GlobalAccessibilityProvider from 'components/shared/GlobalAccessibilityProvider/GlobalAccessibilityProvider';
import SnackBar from 'components/shared/SnackBar';
import Layout from 'components/ui/Layout';
import { PortalContainerProvider } from 'components/ui/Portal';
import Suspense from 'components/ui/Suspense';
import { ROUTES } from 'configs/routes';
import { withAdmin } from 'hocs/withAdmin';
import { withAuth } from 'hocs/withAuth';
import { withMobileAuth } from 'hocs/withMobileAuth';
import { useDisableMobileAppZoom } from 'hooks/useDisableMobileAppZoom';
import RSVRedirect from 'pages/RSVRedirect';
import RootRedirect from 'pages/RootRedirect';
import LogoutRedirectPage from 'pages/auth/LogoutRedirectPage';
import QpParamsStoreInitializer from 'stores/global/qpStore/QpParamsStoreConnector';
import { WidthProvider } from 'utils/devices';
import { callNative } from 'utils/nativeBridge';
import { isMobileWebView } from 'utils/nativeEnv';

const AuthPage = React.lazy(() => import('../auth/AuthPage'));
const LoginRedirectPage = React.lazy(() => import('../auth/LoginRedirectPage'));

const ConstructorPageCourses = withMobileAuth(
  withAuth(withAdmin(React.lazy(() => import('../constructor/ConstructorPageCourses'))))
);
const ConstructorPageChapters = withMobileAuth(
  withAuth(withAdmin(React.lazy(() => import('../constructor/ConstructorPageChapters'))))
);
const ConstructorPageLesson = withMobileAuth(
  withAuth(withAdmin(React.lazy(() => import('../constructor/ConstructorPageLesson'))))
);

const CoursePage = withMobileAuth(withAuth(React.lazy(() => import('../CoursePage'))));

const RootAdminRedirect = withMobileAuth(withAuth(withAdmin(RootRedirect)));

const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes);

const Root: React.FC = () => {
  useDisableMobileAppZoom();

  // const [isRedirect] = React.useState(
  //   () => import.meta.env.PROD && !document.cookie.split(';').some((item) => item.trim().startsWith('RSV_DEBUG=true'))
  // );

  // React.useLayoutEffect(() => {
  //   if (isRedirect) {
  //     window.location.href = 'https://rsv.ru/maintenance';
  //   }
  // }, [isRedirect]);

  // if (isRedirect) {
  //   return null;
  // }

  React.useEffect(() => {
    if (!isMobileWebView()) {
      return;
    }

    const onPopState = () => {
      const historyIndex = window.history.state?.idx ?? 0;
      const noHistoryLeft = historyIndex <= 0 || window.history.length <= 1;

      if (noHistoryLeft) {
        callNative('noHistory');
      }
    };

    /**
     * Глобальный перехватчик кликов по внешним ссылкам.
     * Предотвращает открытие ссылок внутри WebView (чтобы пользователь не застрял),
     * и делегирует их открытие нативному приложению.
     */
    const onGlobalClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      const anchor = target.closest('a');

      if (!anchor || !anchor.href) {
        return;
      }

      const isDownloadLink = anchor.dataset.nativeAction === 'download' || anchor.hasAttribute('download');

      if (isDownloadLink) {
        return;
      }

      // Ссылка считается внешней/требующей открытия вне WebView, если:
      // 1. Явно указан target="_blank"
      // 2. Домен ссылки отличается от текущего домена (например, вставили сырой HTML без target="_blank")
      // 3. Это спец. протоколы типа mailto: или tel:
      const isBlank = anchor.target === '_blank';
      const isExternalHost = anchor.host && anchor.host !== window.location.host;
      const isSpecialProtocol = anchor.protocol === 'mailto:' || anchor.protocol === 'tel:';

      if (isBlank || isExternalHost || isSpecialProtocol) {
        event.preventDefault();
        callNative('onOpenExternalLink', { url: anchor.href });
      }
    };

    window.addEventListener('popstate', onPopState);
    document.addEventListener('click', onGlobalClick, true);

    return () => {
      window.removeEventListener('popstate', onPopState);
      document.removeEventListener('click', onGlobalClick, true);
    };
  }, []);

  return (
    <BrowserRouter>
      <QpParamsStoreInitializer>
        <WidthProvider>
          <PortalContainerProvider>
            <GlobalAccessibilityProvider>
              <Layout>
                <Suspense withLoader>
                  <SentryRoutes>
                    <Route path={ROUTES.root}>
                      <Route path={ROUTES.auth} element={<AuthPage />} />
                      <Route path={ROUTES.loginRedirect} element={<LoginRedirectPage />} />
                      <Route path={ROUTES.logoutRedirect.mask} element={<LogoutRedirectPage />} />

                      <Route path={ROUTES.constructor.lesson.mask} element={<ConstructorPageLesson />} />
                      <Route path={ROUTES.constructor.singleLesson.mask} element={<ConstructorPageLesson />} />

                      <Route path={ROUTES.constructor.courseList.mask} element={<ConstructorPageCourses />} />
                      <Route path={ROUTES.constructor.chapterList.mask} element={<ConstructorPageChapters />} />

                      <Route path={ROUTES.course.root.mask} element={<CoursePage />} />

                      <Route index element={<RootAdminRedirect />} />
                      <Route path={ROUTES.notFound} element={<RSVRedirect />} />
                      <Route path="*" element={<Navigate to={ROUTES.notFound} replace />} />
                    </Route>
                  </SentryRoutes>
                </Suspense>
              </Layout>
            </GlobalAccessibilityProvider>
          </PortalContainerProvider>
        </WidthProvider>
      </QpParamsStoreInitializer>
      <SnackBar />
    </BrowserRouter>
  );
};

export default React.memo(Root);
